<template>
  <div class="publications-form">
    <div class="section-header d-flex justify-content-between align-items-center mb-4">
      <h5 class="section-title">Publications ({{ localForm.publications.length }})</h5>
      <button type="button" class="btn btn-outline-primary btn-sm" @click="addPublication">
        <i class="bi bi-plus me-1"></i>Add Publication
      </button>
    </div>

    <div v-for="(publication, index) in localForm.publications" :key="publicationKey(publication, index)" class="publication-card mb-4">
      <div
        class="publication-header d-flex justify-content-between align-items-center"
        role="button"
        tabindex="0"
        @click="togglePublication(publicationKey(publication, index))"
        @keydown.enter.prevent="togglePublication(publicationKey(publication, index))"
        @keydown.space.prevent="togglePublication(publicationKey(publication, index))"
      >
        <h6 class="mb-0 border-0 pb-0">{{ publicationHeaderTitles[publicationKey(publication, index)] }}</h6>
        <i class="bi" :class="isPublicationExpanded(publicationKey(publication, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>

      <div v-if="isPublicationExpanded(publicationKey(publication, index))" class="card-body mt-3">

        <!-- Title -->
        <div class="mb-3">
          <label class="form-label">Title</label>
          <input
            type="text"
            class="form-control"
            v-model="publication.title"
            placeholder="Publication title"
            @input="updateParent"
          >
        </div>

        <!-- Authors Section -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="form-label mb-0" style="color: #495057;  border-bottom: 1px solid #dee2e6;  padding-bottom: 0.5rem;">Authors</h6>
            <button type="button" class="btn btn-outline-primary btn-sm" @click="addAuthor(index)">
              <i class="bi bi-plus me-1"></i>ADD
            </button>
          </div>

          <div v-for="(author, authorIndex) in publication.authors" :key="`author-${index}-${authorIndex}`" class="nested-item mb-3">
            <div class="nested-header d-flex justify-content-between align-items-center mb-2">
              <span class="nested-title">Author {{ authorIndex + 1 }}</span>
              <button type="button" class="btn btn-sm btn-link text-danger p-1" @click="removeAuthor(index, authorIndex)">
                <i class="bi bi-x"></i>
              </button>
            </div>
            <div class="row">
              <div class="col-md-6">
                <label class="form-label">Name</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="author.name"
                  placeholder="Author name"
                  @input="updateParent"
                >
              </div>
              <div class="col-md-6">
                <label class="form-label">Citation</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="author.citation"
                  placeholder="Author citation"
                  @input="updateParent"
                >
              </div>
            </div>
          </div>
        </div>

        <!-- External Identifiers Section -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="form-label mb-0" style="color: #495057;  border-bottom: 1px solid #dee2e6;  padding-bottom: 0.5rem;">External Identifiers</h6>
            <button type="button" class="btn btn-outline-primary btn-sm" @click="addExternalIdentifier(index)">
              <i class="bi bi-plus me-1"></i>ADD
            </button>
          </div>

          <div v-for="(identifier, identifierIndex) in publication.externalIdentifiers" :key="`identifier-${index}-${identifierIndex}`" class="nested-item mb-3">
            <div class="nested-header d-flex justify-content-between align-items-center mb-2">
              <span class="nested-title">External Identifier {{ identifierIndex + 1 }}</span>
              <button type="button" class="btn btn-sm btn-link text-danger p-1" @click="removeExternalIdentifier(index, identifierIndex)">
                <i class="bi bi-x"></i>
              </button>
            </div>
            <div class="row">
              <div class="col-md-3">
                <label class="form-label">Ext. Identifier e.g. PMID</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="identifier.identifier"
                  placeholder="PMID"
                  @input="updateParent"
                >
              </div>
              <div class="col-md-3">
                <label class="form-label">External ID</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="identifier.externalId"
                  placeholder="12345678"
                  @input="updateParent"
                >
              </div>
              <div class="col-md-3">
                <label class="form-label">Full URL</label>
                <input
                  type="url"
                  class="form-control"
                  v-model="identifier.fullUrl"
                  placeholder="https://..."
                  @input="updateParent"
                >
              </div>
              <div class="col-md-3">
                <label class="form-label">Relationship</label>
                <select
                  class="form-select"
                  v-model="identifier.relationship"
                  @change="updateParent"
                >
                  <option value="">Select relationship</option>
                  <option value="Self">Self</option>
                  <option value="Part of">Part of</option>
                  <option value="Funded by">Funded by</option>
                  <option value="Version of">Version of</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Journal and Date -->
        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label">Journal</label>
            <input
              type="text"
              class="form-control"
              v-model="publication.journal"
              placeholder="Journal name"
              @input="updateParent"
            >
          </div>
          <div class="col-md-6">
            <label class="form-label">Date</label>
            <input
              type="date"
              class="form-control"
              v-model="publication.date"
              @input="updateParent"
            >
          </div>
        </div>

        <!-- Keywords -->
        <div class="form-group mb-3">
          <label class="form-label">Keywords</label>
          <input
            type="text"
            class="form-control"
            v-model="publication.keywords"
            placeholder="Keywords, separated by commas"
            @input="updateParent"
          >
        </div>

        <!-- Content -->
        <div class="form-group">
          <label class="form-label">Content</label>
          <div class="content-editor">
            <div class="editor-toolbar">
              <select class="form-select form-select-sm me-2" style="width: auto; display: inline-block;">
                <option value="normal">Normal</option>
                <option value="heading">Heading</option>
                <option value="subheading">Subheading</option>
              </select>
              <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                      @click="format('bold')">
                <i class="bi bi-type-bold"></i>
              </button>

              <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                      @click="format('italic')">
                <i class="bi bi-type-italic"></i>
              </button>

              <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                      @click="format('underline')">
                <i class="bi bi-type-underline"></i>
              </button>

              <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                      @click="format('insertUnorderedList')">
                <i class="bi bi-list-ul"></i>
              </button>

              <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                      @click="format('insertOrderedList')">
                <i class="bi bi-list-ol"></i>
              </button>
            </div>
            <div
              class="form-control content-textarea"
              contenteditable="true"
              :ref="`editor-${index}`"
              @focus="isEditingContent = true"
              @blur="isEditingContent = false"
              @input="onEditorInput(index)"
            ></div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-12 d-flex justify-content-end">
            <button type="button" class="btn btn-sm btn-outline-danger" @click="removePublication(index)">
              <i class="bi bi-x me-1"></i>Remove
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="localForm.publications.length === 0" class="empty-state text-center py-5">
      <i class="bi bi-journal-text text-muted" style="font-size: 3rem;"></i>
      <p class="text-muted mt-3 mb-0">No publications added yet. Click the Add Publication button to get started.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PublicationsForm',
  props: {
    formData: {
      type: Object,
      required: true
    },
    profileData: {
      type: Object,
    }
  },
  emits: ['update'],
  data() {
    return {
      localForm: this.initializeLocalForm(),
      isEditingContent: false,
      showDebug: false,
      expandedPublicationIds: [],
      initialized: false
    }
  },
  computed: {
    fellowDetailsPublications() {
      return this.profileData?.data?.publications || []
    },
    publicationHeaderTitles() {
      return (this.localForm.publications || []).reduce((titles, publication, index) => {
        const key = this.publicationKey(publication, index)
        const title = this.normalizeHeaderText(publication?.title)
        const journal = this.normalizeHeaderText(publication?.journal)
        const year = this.getYear(publication?.date)

        if (!title && !journal && !year) {
          titles[key] = 'New Publication'
          return titles
        }

        const left = [title, journal].filter(Boolean).join(' | ')
        titles[key] = [left, year].filter(Boolean).join(', ')
        return titles
      }, {})
    }
  },
  watch: {
    formData: {
      handler(newData) {
        if (!this.shouldSyncFromFormData(newData)) return

        const previousExpandedIds = [...this.expandedPublicationIds]
        this.localForm = this.initializeLocalForm(newData)

        if (!this.initialized) {
          this.initializeExpandedPublicationIds()
          this.initialized = true
        } else {
          this.reconcileExpandedPublicationIds(previousExpandedIds)
        }

        if (!this.isEditingContent) {
          this.hydrateEditors()
        }
      },
      deep: true
    },

    profileData: {
      handler() {
        if (!this.shouldSyncFromProfileData()) return

        const previousExpandedIds = [...this.expandedPublicationIds]
        this.localForm = this.initializeLocalForm(this.formData)

        if (!this.initialized) {
          this.initializeExpandedPublicationIds()
          this.initialized = true
        } else {
          this.reconcileExpandedPublicationIds(previousExpandedIds)
        }

        if (!this.isEditingContent) {
          this.hydrateEditors()
        }
      },
      deep: true
    },
  },
  methods: {
    format(command) {
      document.execCommand(command, false, null)
    },

    onEditorInput(index) {
      const ref = this.$refs[`editor-${index}`]
      const editor = Array.isArray(ref) ? ref[0] : ref
      if (!editor) return

      this.localForm.publications[index].content = editor.innerHTML
      this.updateParent()
    },

    hydrateEditors() {
      this.$nextTick(() => {
        this.localForm.publications.forEach((pub, index) => {
          const ref = this.$refs[`editor-${index}`]
          const editor = Array.isArray(ref) ? ref[0] : ref
          if (!editor) return

          editor.innerHTML = pub.content || ''
        })
      })
    },

    initializeLocalForm(data = null) {
      const sourceData = data || this.formData
      const previousPublications = this.localForm?.publications || []

      // Priority 1: Use existing form data if it has publications
      if (sourceData?.publications && Array.isArray(sourceData.publications) && sourceData.publications.length > 0) {
        return {
          publications: sourceData.publications.map((publication, index) => ({
            id: publication.id || previousPublications[index]?.id || null,
            _localKey: previousPublications[index]?._localKey || publication._localKey || this.generateLocalKey(),
            _isNew: previousPublications[index]?._isNew ?? publication._isNew ?? false,
            title: publication.title || '',
            authors: Array.isArray(publication.authors) ? publication.authors : [],
            externalIdentifiers: Array.isArray(publication.externalIdentifiers) ? publication.externalIdentifiers : [],
            journal: publication.journal || '',
            keywords: publication.keywords || '',
            date: publication.date || '',
            content: publication.content || ''
          }))
        }
      }

      // Priority 2: Extract from API response if available
      const fellowDetailsPublications = this.fellowDetailsPublications
      if (Array.isArray(fellowDetailsPublications) && fellowDetailsPublications.length > 0) {
        return {
          publications: fellowDetailsPublications.map((publication, index) => ({
            id: publication.id || previousPublications[index]?.id || null,
            _localKey: previousPublications[index]?._localKey || publication._localKey || this.generateLocalKey(),
            _isNew: previousPublications[index]?._isNew ?? publication._isNew ?? false,
            title: publication.title || '',
            authors: Array.isArray(publication.authors) ? publication.authors : [],
            externalIdentifiers: Array.isArray(publication.externalIdentifiers) ? publication.externalIdentifiers : [],
            journal: publication.journal || '',
            keywords: publication.keywords || '',
            date: publication.date || '',
            content: publication.content || ''
          }))
        }
      }

      // Priority 3: Return empty array
      return {
        publications: []
      }
    },

    updateParent() {
      this.$emit('update', {
        ...this.localForm,
        publications: (this.localForm.publications || []).map(({ _localKey, _isNew, ...publication }) => ({
          ...publication
        }))
      })
    },

    generateLocalKey() {
      return `${Date.now()}-${Math.random()}`
    },

    addPublication() {
      const newPublication = {
        id: null,
        _localKey: this.generateLocalKey(),
        _isNew: true,
        title: '',
        authors: [],
        externalIdentifiers: [],
        journal: '',
        keywords: '',
        date: '',
        content: ''
      }

      this.localForm.publications.push(newPublication)
      const key = this.publicationKey(newPublication, this.localForm.publications.length - 1)
      if (key && !this.expandedPublicationIds.includes(key)) {
        this.expandedPublicationIds.push(key)
      }
      this.updateParent()
    },

    removePublication(index) {
      const removed = this.localForm.publications[index]
      this.localForm.publications.splice(index, 1)
      const removedKey = this.publicationKey(removed, index)
      this.expandedPublicationIds = this.expandedPublicationIds.filter((id) => id !== removedKey)
      this.updateParent()
    },

    publicationKey(publication, index) {
      return publication?.id || publication?._localKey || `publication-${index}`
    },

    togglePublication(key) {
      if (!key) return

      if (this.expandedPublicationIds.includes(key)) {
        this.expandedPublicationIds = this.expandedPublicationIds.filter((id) => id !== key)
      } else {
        this.expandedPublicationIds.push(key)
      }
      this.$nextTick(() => this.hydrateEditors())
    },

    isPublicationExpanded(key) {
      return this.expandedPublicationIds.includes(key)
    },

    initializeExpandedPublicationIds() {
      this.reconcileExpandedPublicationIds(this.expandedPublicationIds)
    },

    shouldSyncFromFormData(newData) {
      if (!newData) return false

      const incomingPublications = Array.isArray(newData.publications) ? newData.publications : []

      if (!this.initialized) return true

      // Keep local editing state stable; only resync for clear/reset scenarios.
      return incomingPublications.length === 0 && this.localForm.publications.length > 0
    },

    shouldSyncFromProfileData() {
      if (!this.initialized) return true

      const profilePublications = this.fellowDetailsPublications
      const hasProfilePublications = Array.isArray(profilePublications) && profilePublications.length > 0

      // If local form is empty but profile data exists, allow backfill.
      return this.localForm.publications.length === 0 && hasProfilePublications
    },

    reconcileExpandedPublicationIds(previousExpandedIds = []) {
      const availableKeys = new Set((this.localForm.publications || []).map((publication, index) => this.publicationKey(publication, index)))
      this.expandedPublicationIds = previousExpandedIds.filter((id) => availableKeys.has(id))
    },

    normalizeHeaderText(value) {
      if (typeof value !== 'string') return ''
      const cleaned = value.trim().replace(/\s+/g, ' ')
      if (!cleaned) return ''

      return cleaned
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    getYear(value) {
      if (!value) return ''
      const asString = String(value).trim()
      const match = asString.match(/^(\d{4})/)
      return match ? match[1] : ''
    },

    addAuthor(publicationIndex) {
      this.localForm.publications[publicationIndex].authors.push({
        name: '',
        citation: ''
      })
      this.updateParent()
    },

    removeAuthor(publicationIndex, authorIndex) {
      this.localForm.publications[publicationIndex].authors.splice(authorIndex, 1)
      this.updateParent()
    },

    addExternalIdentifier(publicationIndex) {
      this.localForm.publications[publicationIndex].externalIdentifiers.push({
        identifier: '',
        externalId: '',
        fullUrl: '',
        relationship: ''
      })
      this.updateParent()
    },

    removeExternalIdentifier(publicationIndex, identifierIndex) {
      this.localForm.publications[publicationIndex].externalIdentifiers.splice(identifierIndex, 1)
      this.updateParent()
    }
  },

  mounted() {
    this.initialized = (this.localForm.publications || []).length > 0
    this.initializeExpandedPublicationIds()
    this.hydrateEditors()
  }
}
</script>

<style scoped>
.h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
}

.publications-form {
  min-height: 400px;
}

/* === Section Header (same as ProjectsForm) === */
.section-header {
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #17a2b8;
}

.section-title {
  color: #17a2b8;
  font-weight: 600;
  font-size: 1.1rem;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* === Publication Container === */
.publication-card {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.publication-list-row {
  gap: 0.75rem;
}

.publication-list-summary {
  min-width: 0;
}

.publication-row-actions {
  flex-shrink: 0;
}

/* Remove card header background, align with Projects look */
.card-header {
  background-color: transparent;
  border-bottom: none;
  padding: 0;
  margin-bottom: 1rem;
}

.card-title {
  font-weight: 600;
  color: #495057;
  font-size: 1rem;
  margin: 0;
}

/* === Input + Select Fields === */
.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #495057;
}

.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.form-control:focus,
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

/* === Subsections (Authors, External Identifiers) === */
.subsection {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.subsection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.nested-item {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
}

/* Nested Titles (Author 1, etc.) */
.nested-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.nested-title {
  font-weight: 500;
  color: #495057;
}

/* === Buttons (identical to ProjectsForm) === */
.btn-outline-primary {
  color: #17a2b8;
  border-color: #17a2b8;
  font-size: 0.85rem;
}

.btn-outline-primary:hover {
  background-color: #17a2b8;
  color: #fff;
}

.btn-outline-danger {
  border-color: #dc3545;
  color: #dc3545;
}

.btn-outline-danger:hover {
  background-color: #dc3545;
  color: white;
}

.btn-link {
  color: #dc3545;
  text-decoration: none;
  padding: 0.25rem;
}

.btn-link:hover {
  color: #a71d2a;
}

/* === Content Editor (simplified to match textarea style) === */
.content-editor {
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.editor-toolbar {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 0.5rem;
}

.content-textarea {
  border: none;
  padding: 0.75rem;
  resize: vertical;
  font-size: 0.9rem;
}

.content-textarea:focus {
  outline: none;
  box-shadow: none;
}

/* === Empty State === */
.empty-state {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background-color: #f8f9fa;
  color: #6c757d;
}

.text-muted {
  font-size: 0.875rem;
}
</style>
